/**
 * Canonical `.upg` serialisation, the `upg fmt` reference implementation.
 *
 * The same logical graph always serialises to byte-identical output, regardless
 * of which tool wrote it. This is the linchpin of UPG-577: every writer (MCP
 * server, CLI, SDK, cloud export, AI agents via MCP) calls this one serialiser,
 * so git diffs reflect MEANING, not formatting.
 *
 * Anchored on RFC 8785 (JSON Canonicalization Scheme) for the
 * object-internal rules, with two deliberate deviations for the git-review
 * lifecycle: (1) pretty-print (2-space, one element per line, LF) rather than
 * JCS's compact single line; (2) semantic sort of the set-like arrays
 * (`nodes`, `edges`, `cross_edges`, `tags`) rather than JCS's preserve-as-is.
 *
 * https://unifiedproductgraph.org/spec | MIT
 */

// Namespace import (not `import { createHash }`): a named import binds the
// member at module-load, which throws in browser bundlers that externalise
// `node:crypto` (e.g. Vite-based apps that import this package for its type
// registry and property schema on the client). A namespace defers the
// `.createHash` access to call-time — only Node writers (CLI/MCP/SDK/cloud)
// ever call it, never browsers.
import * as nodeCrypto from 'node:crypto'
import type { UPGBaseNode } from '../shapes/base-node.js'
import type { UPGEdge } from '../shapes/edges.js'
import type {
  UPGDocument,
  UPGPortfolioDocument,
  UPGCrossEdge,
  UPGProduct,
  UPGProductStage,
} from '../shapes/document.js'

/**
 * The on-disk canonical serialisation version, written to `$upg.format_version`.
 * Distinct from `UPG_VERSION` (the catalogue/spec version, written to
 * `$upg.spec_version`): the *serialisation* can evolve independently of the
 * *schema*. Bumped to 1.0.0 for the canonical-form + `$upg` header release
 * (UPG-577), the first format version actually written to disk.
 */
export const UPG_CANONICAL_FORMAT_VERSION = '1.0.0' as const

/** Underlying cryptographic hash primitive used to compute the digest. */
const INTEGRITY_HASH_PRIMITIVE = 'sha256' as const
/** Hex chars of digest retained: 32 hex = 128 bits (a truncation of SHA-256). */
const INTEGRITY_DIGEST_HEX = 32
/**
 * Public label for the integrity digest, recorded in `$upg.integrity.algorithm`.
 * It is SHA-256 truncated to 128 bits, so the label must say so: a reader that
 * recomputes a full `sha256` would get 64 chars and never match the stored 32.
 * The "sha256-128" form mirrors the SHA-512/256 truncation convention.
 */
const INTEGRITY_ALGORITHM = 'sha256-128' as const

// ─── The `$upg` header ──────────────────────────────────────────────────────

/** Provenance block inside the `$upg` header. Holds the volatile fields. */
export interface UPGHeaderProvenance {
  /** The tool that produced this document */
  tool: string
  /** Optional tool version */
  tool_version?: string
  /** ISO 8601 timestamp of export (volatile, excluded from the integrity body) */
  exported_at?: string
  /** Optional workspace/project identifier in the source tool */
  workspace_id?: string
}

/** Integrity block inside the `$upg` header, checksum of the canonical BODY. */
export interface UPGHeaderIntegrity {
  /** Hash algorithm label, e.g. "sha256-128" (SHA-256 truncated to 128 bits) */
  algorithm: string
  /** Hex checksum of the canonical serialisation of the body (product + nodes + edges) */
  body: string
}

/**
 * The reserved `$upg` header object. Consolidates the previously-scattered
 * metadata (`upg_version`, `source`, `exported_at`, `_integrity`) into one
 * leading object so a reader gets instant orientation and tools can read
 * metadata without parsing the whole graph.
 */
export interface UPGHeader {
  /** Serialisation/format version (this serialiser's contract) */
  format_version: string
  /** Spec (catalogue) version the graph conforms to */
  spec_version: string
  /** "portfolio" for portfolio documents; omitted for single-product */
  kind?: 'portfolio'
  /**
   * Workspace member kind for single-product graphs (0.10.0, #45): `org_rollup`
   * (company umbrella), `watched` (monitored intelligence graph), or
   * `operating_function` (a function a team operates, not a product it ships;
   * 0.17.0). Omitted for ordinary products (the default). Distinct from `kind`,
   * which is the portfolio-vs-product document discriminator.
   */
  member_kind?: 'org_rollup' | 'watched' | 'operating_function'
  /** Summary mirror of the root product (single-product docs) */
  product?: { id: string; title: string; stage?: string }
  /** Summary mirror of the organisation (portfolio docs) */
  organization?: { id: string; title: string }
  /** One-line description, mirrored from the product/org for at-a-glance orientation */
  summary?: string
  /** Element counts, for cheap size reads without walking the graph */
  counts: Record<string, number>
  /** Provenance, who/what last wrote this, and when (volatile) */
  provenance: UPGHeaderProvenance
  /** Tamper-evidence over the canonical body */
  integrity: UPGHeaderIntegrity
}

// ─── JCS-style canonicalisation of open values ───────────────────────────────

/**
 * Recursively canonicalise an arbitrary JSON value per RFC 8785 object rules:
 * object keys sorted ascending by UTF-16 code unit; array element ORDER
 * preserved (arrays are meaningful sequences); scalars untouched. Used for
 * open objects (`properties`) and any nested value we do not pin by hand.
 */
function canonicalizeOpen(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalizeOpen)
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const key of Object.keys(value as Record<string, unknown>).sort(byCodeUnit)) {
      out[key] = canonicalizeOpen((value as Record<string, unknown>)[key])
    }
    return out
  }
  return value
}

/** Code-unit (UTF-16) ascending comparison, NEVER locale-aware (must be stable across machines). */
function byCodeUnit(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** True when a value is "empty" and should be omitted from canonical output (ADR A.5). */
function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.length === 0
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value as object).length === 0
  return false
}

/**
 * Build an object with keys emitted in `keyOrder` first (skipping empty
 * optionals), then any remaining keys JCS-sorted. `forceKeys` are emitted even
 * when empty (the required identity fields). Values for keys named in
 * `openKeys` are canonicalised recursively (open objects like `properties`).
 */
function orderedObject(
  source: Record<string, unknown>,
  keyOrder: string[],
  opts: { forceKeys?: string[]; openKeys?: string[] } = {},
): Record<string, unknown> {
  const force = new Set(opts.forceKeys ?? [])
  const open = new Set(opts.openKeys ?? [])
  const out: Record<string, unknown> = {}
  const emit = (key: string) => {
    if (!(key in source)) return
    const raw = source[key]
    if (!force.has(key) && isEmpty(raw)) return
    out[key] = open.has(key) ? canonicalizeOpen(raw) : raw
  }
  for (const key of keyOrder) emit(key)
  for (const key of Object.keys(source).sort(byCodeUnit)) {
    if (!keyOrder.includes(key)) emit(key)
  }
  return out
}

// ─── Drift repair (ADR A.6) ──────────────────────────────────────────────────

/**
 * Repair double-encoded JSON drift: some historical writers stored
 * `properties`/`tags` as JSON *strings* instead of structured values. If a
 * field that must be an object/array holds a string that parses to the
 * expected type, restructure it (idempotent). Otherwise throw, never guess.
 */
function repairNodeDrift(node: UPGBaseNode): UPGBaseNode {
  const out: UPGBaseNode = { ...node }
  if (typeof out.properties === 'string') {
    out.properties = parseDriftString(out.properties, 'object', `node ${node.id}.properties`) as Record<
      string,
      unknown
    >
  }
  if (typeof (out.tags as unknown) === 'string') {
    out.tags = parseDriftString(out.tags as unknown as string, 'array', `node ${node.id}.tags`) as string[]
  }
  return out
}

function parseDriftString(raw: string, expect: 'object' | 'array', where: string): unknown {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    throw new Error(`[upg fmt] ${where} is a string that is not valid JSON: ${truncate(raw)}`)
  }
  const ok = expect === 'array' ? Array.isArray(parsed) : parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
  if (!ok) throw new Error(`[upg fmt] ${where} is a string that does not parse to a JSON ${expect}: ${truncate(raw)}`)
  return parsed
}

function truncate(s: string): string {
  return s.length > 60 ? s.slice(0, 57) + '...' : s
}

/**
 * Derive the one-line `$upg.summary` from a description: first non-empty line,
 * capped. Kept short on purpose, the header is orientation, not a copy of the
 * product body. Always re-derived on serialise, so it cannot drift.
 */
function deriveSummary(description?: string): string | undefined {
  if (!description) return undefined
  const firstLine = description.split('\n').map((l) => l.trim()).find((l) => l.length > 0)
  if (!firstLine) return undefined
  return firstLine.length > 200 ? firstLine.slice(0, 197) + '...' : firstLine
}

// ─── Element ordering for the set-like arrays (ADR A.2) ──────────────────────

function sortNodes(nodes: UPGBaseNode[]): UPGBaseNode[] {
  return [...nodes].sort((a, b) =>
    byCodeUnit(a.type ?? '', b.type ?? '') ||
    byCodeUnit(a.slug ?? a.title ?? '', b.slug ?? b.title ?? '') ||
    byCodeUnit(a.id ?? '', b.id ?? ''),
  )
}

function sortEdges<T extends { source: string; target: string; type: string; id: string }>(edges: T[]): T[] {
  return [...edges].sort((a, b) =>
    byCodeUnit(a.source ?? '', b.source ?? '') ||
    byCodeUnit(a.target ?? '', b.target ?? '') ||
    byCodeUnit(a.type ?? '', b.type ?? '') ||
    byCodeUnit(a.id ?? '', b.id ?? ''),
  )
}

// ─── Per-object canonical key order (ADR A.1) ────────────────────────────────

/**
 * Canonical serialisation order for a node's top-level keys.
 *
 * Deliberately a SUPERSET of `UPG_BASE_NODE_FIELDS`: it also orders tolerated
 * non-base keys (`lifecycle_status`, `sort_order`) so that a graph carrying one
 * still serialises deterministically. `base-node-fields.test.ts` asserts the
 * subset relation in the other direction, which is the one that can regress: a
 * base field declared and never added here would silently drop out of canonical
 * ordering. Never assert equality, which would fail on the two tolerated keys
 * and push the next author to delete them.
 *
 * Exported at 0.33.0 so that assertion can be written; consumers that need to
 * classify base fields use `UPG_BASE_NODE_FIELDS`, not this.
 */
export const NODE_KEY_ORDER = [
  'id', 'type', 'title', 'slug', 'aliases', 'key', 'description', 'tags', 'status',
  'archived', 'archived_at',
  'lifecycle_status', 'source_id', 'source_type', 'mapping_confidence',
  'external_tool', 'external_ref', 'external_id', 'external_links',
  'created_at', 'updated_at', 'sort_order', 'properties',
]
/**
 * Canonical key order for a within-graph edge.
 *
 * @remarks
 * EXPORTED AT 0.34.0 so the subset assertion can be written, exactly as
 * `NODE_KEY_ORDER` was exported at 0.33.0 and for the same reason. K1 of the
 * 0.33.0 bundle fixed this class for nodes and left both edge twins as
 * hand-maintained, unexported, unasserted consts — and the very next release
 * added a field to `UPGEdge`, which is the regression the node-side assertion
 * exists to prevent. `canonical-format.test.ts` asserts every declared `UPGEdge`
 * key appears here. A SUBSET, never equality, for the reason the node comment
 * gives: an equality check would fail on tolerated keys and push the next author
 * to delete them.
 */
export const EDGE_KEY_ORDER = ['id', 'source', 'target', 'type', 'mapping_confidence', 'provenance', 'properties']
/**
 * Canonical key order for a portfolio cross-product edge. The third instance of
 * the class `NODE_KEY_ORDER` fixed at 0.33.0, exported at 0.34.0 with the same
 * subset assertion.
 *
 * @remarks
 * NO `provenance` ENTRY, deliberately. 0.34.0 added `provenance?` to `UPGEdge`
 * and NOT to `UPGCrossEdge`, so an entry here would order a key that cannot be
 * written. The assertion is the point of touching this const at all: it is the
 * direction that can regress, and the next field added to `UPGCrossEdge` now
 * cannot go unordered.
 *
 * `properties` WAS MISSING UNTIL 0.34.0, and the new assertion found it on its
 * first run. It has been declared on `UPGCrossEdge` since 0.10.0 (the parity
 * assessment a `feature_rivals_competitor_feature` cross-edge carries), and it
 * fell through to the sorted-tail pass instead: deterministic, so nothing was
 * corrupt, but positioned alphabetically among unlisted keys rather than in the
 * slot the shape implies. That is precisely the regression the node twin got a
 * guard for at 0.33.0, sitting unnoticed in the edge twin for eleven minors.
 *
 * Measured before changing it, because a canonical-order edit rewrites bytes:
 * across all 61 cross-edges in the estate, ZERO carry `properties`. So no file
 * re-serialises differently and the fix is free today, which it would not have
 * been after the first parity assessment landed.
 */
export const CROSS_EDGE_KEY_ORDER = ['id', 'source', 'target', 'type', 'source_product_id', 'target_product_id', 'mapping_confidence', 'properties', 'alias', 'relevance', 'audience_role']
const PRODUCT_KEY_ORDER = ['id', 'title', 'description', 'stage', 'properties']

function canonicalNode(node: UPGBaseNode): Record<string, unknown> {
  const repaired = repairNodeDrift(node) as unknown as Record<string, unknown>
  // tags: sort + de-duplicate (unordered label set). aliases: preserve order (append-only history).
  if (Array.isArray(repaired.tags)) {
    repaired.tags = [...new Set(repaired.tags as string[])].sort(byCodeUnit)
  }
  return orderedObject(repaired, NODE_KEY_ORDER, { forceKeys: ['id', 'type', 'title'], openKeys: ['properties'] })
}

function canonicalEdge(edge: UPGEdge): Record<string, unknown> {
  return orderedObject(edge as unknown as Record<string, unknown>, EDGE_KEY_ORDER, {
    forceKeys: ['id', 'source', 'target', 'type'],
    openKeys: ['properties'],
  })
}

function canonicalCrossEdge(edge: UPGCrossEdge): Record<string, unknown> {
  return orderedObject(edge as unknown as Record<string, unknown>, CROSS_EDGE_KEY_ORDER, {
    forceKeys: ['id', 'source', 'target', 'type'],
    // `properties` is an OPEN bag and must be canonicalised like every other one
    // (node, edge, product all pass it). Without this, two writers emitting the
    // same parity assessment with their keys in a different order produce
    // different bytes, which breaks contract 3 of this format: input key order
    // does not change the output. Free to fix today and measured as such: zero
    // of the 61 cross-edges in the estate carry properties.
    openKeys: ['properties'],
  })
}

function canonicalProduct(product: Record<string, unknown>): Record<string, unknown> {
  return orderedObject(product, PRODUCT_KEY_ORDER, { forceKeys: ['id', 'title'], openKeys: ['properties'] })
}

/**
 * Reconcile the root product summary against its canonical node.
 *
 * The product lives in two places: the root `doc.product` summary (mirrored into
 * the `$upg` header) and, once graph tools operate on it, a `type: 'product'`
 * node in `doc.nodes` sharing the product's id. Graph edits (`update_node`, batch
 * ops) touch the node, so the root summary and the header drift, a product can
 * read `concept` / "304 types" in the header while the node already says `launch`
 * / "313 types". Treat the node as the source of truth and overlay its live
 * title, description, and stage onto the root, so the body block, the derived
 * summary, and the header all re-derive from current data on every write. This
 * is self-healing: a file that already drifted is corrected on its next write.
 * No matching node (product is root-only): returned unchanged.
 *
 * STAGE AUTHORITY (0.30.1). Because this overlay runs on EVERY serialize, the
 * node — not the summary — is the authoritative store of a product's stage, and
 * `$upg.product.stage` / `doc.product.stage` are its denormalised projection.
 * The corollary is a rule every writer must obey: **a stage write that touches
 * only the summary is a no-op on disk**, because this function re-derives the
 * summary from the untouched node before the bytes are written. `updateProduct`
 * and `update_node` therefore write the node carriers too (sdk `lib/workspace.ts`,
 * `lib/tools.ts` §B). Resolution order below is the SAME order those writers and
 * `get_graph_digest` read in — `properties.stage`, then `status` — so the
 * serialiser can never disagree with what a read reports. (Two different
 * precedence orders in one system is precisely how a silent no-op survives:
 * the writer synced the header from `properties.stage` while the serialiser
 * overlaid `status` back over it.)
 */
function effectiveRootProduct(doc: UPGDocument): UPGProduct {
  const node = doc.nodes?.find((n) => n.type === 'product' && n.id === doc.product.id)
  if (!node) return doc.product
  // A product's lifecycle status IS its stage, the same axis (grammar/lifecycles),
  // and `properties.stage` is the spec's declared carrier for it on a product node
  // (the `properties.stage → status` lift was deliberately retired in 0.9.10 #33
  // because product stage is its own 9-phase axis). Prefer the declared carrier,
  // fall back to `status`, then to the summary.
  const propStage = (node.properties as Record<string, unknown> | undefined)?.stage
  const nodeStage =
    (typeof propStage === 'string' ? (propStage as UPGProductStage) : undefined) ??
    (node.status as UPGProductStage | undefined)
  return {
    ...doc.product,
    title: node.title ?? doc.product.title,
    description: node.description ?? doc.product.description,
    stage: nodeStage ?? doc.product.stage,
  }
}

// ─── Body assembly + checksum ────────────────────────────────────────────────

/** The canonical body of a single-product doc: product + sorted nodes + sorted edges. */
function singleBody(doc: UPGDocument): Record<string, unknown> {
  return {
    product: canonicalProduct(effectiveRootProduct(doc) as unknown as Record<string, unknown>),
    nodes: sortNodes(doc.nodes ?? []).map(canonicalNode),
    edges: sortEdges(doc.edges ?? []).map(canonicalEdge),
  }
}

/** The canonical body of a portfolio doc. */
function portfolioBody(doc: UPGPortfolioDocument): Record<string, unknown> {
  const products = [...(doc.products ?? [])]
    .sort((a, b) => byCodeUnit(a.id ?? '', b.id ?? ''))
    .map((p) => {
      const { nodes, edges, ...rest } = p
      return {
        ...canonicalProduct(rest as unknown as Record<string, unknown>),
        nodes: sortNodes(nodes ?? []).map(canonicalNode),
        edges: sortEdges(edges ?? []).map(canonicalEdge),
      }
    })
  return {
    organization: orderedObject(doc.organization as unknown as Record<string, unknown>, ['id', 'title', 'description', 'logo_url', 'industry'], { forceKeys: ['id', 'title'] }),
    product_areas: [...(doc.product_areas ?? [])]
      .sort((a, b) => byCodeUnit(a.id ?? '', b.id ?? ''))
      .map((a) => orderedObject(a as unknown as Record<string, unknown>, ['id', 'title', 'description', 'parent_area_id', 'strategic_priority', 'products'], { forceKeys: ['id', 'title'] })),
    portfolios: [...(doc.portfolios ?? [])]
      .sort((a, b) => byCodeUnit(a.id ?? '', b.id ?? ''))
      .map((p) => orderedObject(p as unknown as Record<string, unknown>, ['id', 'title', 'description', 'parent_portfolio_id', 'hierarchy_model', 'products'], { forceKeys: ['id', 'title'] })),
    products,
    cross_edges: sortEdges(doc.cross_edges ?? []).map(canonicalCrossEdge),
    // Registry tier (shared vocabulary). Emitted only when non-empty so existing
    // portfolio files without a registry stay byte-identical. Canonical entities
    // are normal nodes, serialised with the same node/edge canonical rules.
    ...(doc.registry && doc.registry.nodes.length > 0
      ? {
          registry: {
            nodes: sortNodes(doc.registry.nodes).map(canonicalNode),
            ...(doc.registry.edges && doc.registry.edges.length > 0
              ? { edges: sortEdges(doc.registry.edges).map(canonicalEdge) }
              : {}),
          },
        }
      : {}),
    // Append-only classification-history stream (0.11.0). Emitted only when
    // non-empty so existing portfolio files without it stay byte-identical.
    // Each entry is a competitor_signal node (serialised with the node rules).
    ...(doc.signals && doc.signals.length > 0
      ? { signals: sortNodes(doc.signals).map(canonicalNode) }
      : {}),
  }
}

// ─── Header counts derivation ───────────────────────────────────────────────
//
// `$upg.counts` is DERIVED data: a cheap size read that must always equal the
// body it sits above. It is written by the serialiser and — since the
// `checkHeaderSeal` addition — read back by verifiers. Both call the SAME
// derivation below, so the writer and the verifier can never disagree about
// what the counts should be. (Before this extraction the derivation was inline
// in the two serialise paths and nothing read it back, which is precisely how a
// git-merged file could declare 1274 nodes while holding 1275 and still pass
// every drift class clean.)

/** Element counts for a single-product document, as stamped into `$upg.counts`. */
export function deriveSingleCounts(doc: UPGDocument): Record<string, number> {
  return { nodes: doc.nodes?.length ?? 0, edges: doc.edges?.length ?? 0 }
}

/**
 * Element counts for a portfolio document, as stamped into `$upg.counts`.
 *
 * `products` counts only `product`-kind members (0.10.0, #45): a watched
 * competitor-intelligence graph, the org_rollup umbrella graph, or an
 * operating_function graph (0.17.0) is registered for reference but is not a
 * product under management. Members carry `member_kind` (absent = product,
 * back-compat); the non-product kinds are surfaced separately so the breakdown
 * stays legible, and are OMITTED when zero so existing portfolio files without
 * them stay byte-identical.
 */
export function derivePortfolioCounts(doc: UPGPortfolioDocument): Record<string, number> {
  const members = (doc.products ?? []) as Array<{ member_kind?: string }>
  const memberKindOf = (p: { member_kind?: string }) => p.member_kind ?? 'product'
  const watchedCount = members.filter((p) => memberKindOf(p) === 'watched').length
  const rollupCount = members.filter((p) => memberKindOf(p) === 'org_rollup').length
  const operatingFunctionCount = members.filter((p) => memberKindOf(p) === 'operating_function').length
  return {
    products: members.filter((p) => memberKindOf(p) === 'product').length,
    ...(watchedCount > 0 ? { watched_products: watchedCount } : {}),
    ...(rollupCount > 0 ? { org_rollups: rollupCount } : {}),
    ...(operatingFunctionCount > 0 ? { operating_functions: operatingFunctionCount } : {}),
    product_areas: doc.product_areas?.length ?? 0,
    portfolios: doc.portfolios?.length ?? 0,
    cross_edges: doc.cross_edges?.length ?? 0,
  }
}

/** Dispatching form of the two count derivations above. */
export function deriveCounts(doc: UPGDocument | UPGPortfolioDocument): Record<string, number> {
  return isPortfolio(doc)
    ? derivePortfolioCounts(doc as UPGPortfolioDocument)
    : deriveSingleCounts(doc as UPGDocument)
}

/**
 * Deterministic checksum of the canonical body (volatile fields excluded by
 * construction, the body has no timestamps). Hex, first 32 chars.
 */
export function computeBodyChecksum(doc: UPGDocument | UPGPortfolioDocument): string {
  const body = isPortfolio(doc) ? portfolioBody(doc) : singleBody(doc as UPGDocument)
  // JSON.stringify with no indent: the hash input only needs to be deterministic,
  // and key order is already canonical from the body builders.
  const content = JSON.stringify(body)
  return nodeCrypto.createHash(INTEGRITY_HASH_PRIMITIVE).update(content).digest('hex').slice(0, INTEGRITY_DIGEST_HEX)
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function isPortfolio(doc: UPGDocument | UPGPortfolioDocument): doc is UPGPortfolioDocument {
  return (doc as UPGPortfolioDocument).type === 'portfolio' || 'cross_edges' in doc
}

export interface SerializeOptions {
  /** Override the export timestamp (default: omitted; writers set provenance.exported_at). */
  exportedAt?: string
  /** Who/what is writing (maps to provenance). Falls back to the doc's `source`. */
  source?: { tool: string; tool_version?: string; workspace_id?: string }
}

/**
 * Serialise a UPG document to its canonical on-disk form (the `$upg` header
 * envelope). Idempotent and writer-agnostic: the same logical graph always
 * yields byte-identical output. Always ends with a single trailing newline.
 */
export function serializeCanonical(
  doc: UPGDocument | UPGPortfolioDocument,
  opts: SerializeOptions = {},
): string {
  if (isPortfolio(doc)) return serializePortfolioWithHeader(doc as UPGPortfolioDocument, opts)
  return serializeSingleWithHeader(doc as UPGDocument, opts)
}

/** Build the volatile-aware provenance block shared by both envelopes. */
function buildProvenance(doc: UPGDocument | UPGPortfolioDocument, opts: SerializeOptions): Record<string, unknown> {
  const source = opts.source ?? doc.source ?? { tool: 'unknown' }
  return orderedObject(
    {
      tool: source.tool,
      tool_version: source.tool_version,
      workspace_id: source.workspace_id,
      exported_at: opts.exportedAt ?? doc.exported_at,
    },
    ['tool', 'tool_version', 'workspace_id', 'exported_at'],
    { forceKeys: ['tool'] },
  )
}

/** Single-product canonical form: the `$upg` header envelope (Layer 1 + Layer 2). */
function serializeSingleWithHeader(doc: UPGDocument, opts: SerializeOptions): string {
  const body = singleBody(doc)
  const product = effectiveRootProduct(doc)
  const header: Record<string, unknown> = {
    format_version: UPG_CANONICAL_FORMAT_VERSION,
    spec_version: doc.upg_version,
    product: orderedObject(
      { id: product.id, title: product.title, stage: product.stage },
      ['id', 'title', 'stage'],
      { forceKeys: ['id', 'title'] },
    ),
  }
  const summary = deriveSummary(product.description)
  if (summary) header.summary = summary
  header.counts = deriveSingleCounts(doc)
  // Member kind (0.10.0, #45): stamp non-default kinds so the graph carries its
  // own posture (org_rollup / watched / operating_function); ordinary products
  // stay clean (absent).
  if (
    doc.member_kind === 'org_rollup' ||
    doc.member_kind === 'watched' ||
    doc.member_kind === 'operating_function'
  ) {
    header.member_kind = doc.member_kind
  }
  header.provenance = buildProvenance(doc, opts)
  header.integrity = { algorithm: INTEGRITY_ALGORITHM, body: computeBodyChecksum(doc) }

  return JSON.stringify({ $upg: header, ...body }, null, 2) + '\n'
}

/** Portfolio canonical form: the `$upg` header envelope (kind: "portfolio"). */
function serializePortfolioWithHeader(doc: UPGPortfolioDocument, opts: SerializeOptions): string {
  const body = portfolioBody(doc)
  const org = doc.organization
  const header: Record<string, unknown> = {
    format_version: UPG_CANONICAL_FORMAT_VERSION,
    spec_version: doc.upg_version,
    kind: 'portfolio',
    organization: orderedObject(
      { id: org.id, title: org.title },
      ['id', 'title'],
      { forceKeys: ['id', 'title'] },
    ),
  }
  const summary = deriveSummary(org.description)
  if (summary) header.summary = summary
  // See derivePortfolioCounts for the member-kind rules behind `counts.products`.
  header.counts = derivePortfolioCounts(doc)
  header.provenance = buildProvenance(doc, opts)
  header.integrity = { algorithm: INTEGRITY_ALGORITHM, body: computeBodyChecksum(doc) }

  return JSON.stringify({ $upg: header, ...body }, null, 2) + '\n'
}

/**
 * Parse a `.upg` file's text into the in-memory `UPGDocument` /
 * `UPGPortfolioDocument` (flat) shape, accepting BOTH the canonical `$upg`
 * envelope and the legacy flat envelope. Drift (A.6) is repaired on the way in,
 * so a parse → serialise round-trip is clean. This is the one read path.
 */
export function parseUpg(text: string): UPGDocument | UPGPortfolioDocument {
  return normalizeDocument(JSON.parse(text))
}

/**
 * Normalise an already-parsed object (canonical `$upg` envelope OR legacy flat)
 * into the flat in-memory document shape, repairing drift.
 */
export function normalizeDocument(obj: unknown): UPGDocument | UPGPortfolioDocument {
  const raw = obj as Record<string, unknown>
  const header = raw.$upg as UPGHeader | undefined

  // Resolve the metadata regardless of envelope.
  const upg_version = (header?.spec_version ?? raw.upg_version) as string
  const provenance = header?.provenance
  const exported_at = (provenance?.exported_at ?? raw.exported_at) as string | undefined
  const source = provenance
    ? { tool: provenance.tool, tool_version: provenance.tool_version, workspace_id: provenance.workspace_id }
    : (raw.source as UPGDocument['source'])
  // For canonical ($upg) files, integrity lives in `$upg.integrity.body` and is
  // recomputed on every serialise, we deliberately do NOT reconstruct the
  // legacy `_integrity` field here (its checksum uses a different algorithm; a
  // mismatch would falsely flag the file as tampered). Legacy flat files keep
  // their `_integrity` untouched so the existing tamper-detection path is intact.
  const _integrity = header ? undefined : (raw._integrity as UPGDocument['_integrity'])

  const isPortfolioDoc = raw.type === 'portfolio' || header?.kind === 'portfolio' || 'cross_edges' in raw

  if (isPortfolioDoc) {
    const out: UPGPortfolioDocument = {
      upg_version,
      type: 'portfolio',
      exported_at: exported_at ?? '',
      source: (source as UPGPortfolioDocument['source']) ?? { tool: 'unknown' },
      organization: raw.organization as UPGPortfolioDocument['organization'],
      product_areas: (raw.product_areas as UPGPortfolioDocument['product_areas']) ?? [],
      portfolios: (raw.portfolios as UPGPortfolioDocument['portfolios']) ?? [],
      products: ((raw.products as UPGPortfolioDocument['products']) ?? []).map((p) => ({
        ...p,
        nodes: (p.nodes ?? []).map((n) => repairNodeDrift(n)),
        edges: p.edges ?? [],
      })),
      cross_edges: (raw.cross_edges as UPGPortfolioDocument['cross_edges']) ?? [],
    }
    // Registry tier: read back when present, repairing node drift on the
    // canonical entities the same way product nodes are repaired. Absent on
    // legacy portfolios — left undefined so the field stays optional.
    const rawRegistry = raw.registry as
      | { nodes?: UPGBaseNode[]; edges?: UPGEdge[] }
      | undefined
    if (rawRegistry && Array.isArray(rawRegistry.nodes)) {
      out.registry = {
        nodes: rawRegistry.nodes.map((n) => repairNodeDrift(n)),
        ...(Array.isArray(rawRegistry.edges) ? { edges: rawRegistry.edges } : {}),
      }
    }
    // Append-only classification-history stream (0.11.0). Read back when
    // present; absent on portfolios that have never recorded a move, so the
    // field stays optional.
    const rawSignals = raw.signals as UPGBaseNode[] | undefined
    if (Array.isArray(rawSignals) && rawSignals.length > 0) {
      out.signals = rawSignals.map((n) => repairNodeDrift(n))
    }
    return out
  }

  const out: UPGDocument = {
    upg_version,
    exported_at: exported_at ?? '',
    source: (source as UPGDocument['source']) ?? { tool: 'unknown' },
    product: raw.product as UPGDocument['product'],
    nodes: ((raw.nodes as UPGBaseNode[]) ?? []).map((n) => repairNodeDrift(n)),
    edges: (raw.edges as UPGEdge[]) ?? [],
  }
  // Lift $upg.member_kind back into the in-memory doc (0.10.0, #45). Legacy flat
  // files may carry a top-level member_kind; read either.
  const memberKind = (header?.member_kind ?? raw.member_kind) as UPGDocument['member_kind'] | undefined
  if (memberKind === 'org_rollup' || memberKind === 'watched' || memberKind === 'operating_function') {
    out.member_kind = memberKind
  }
  if (_integrity) out._integrity = _integrity
  return out
}

/**
 * The `upg fmt` operation on raw text: parse (either envelope) → re-serialise
 * canonical. Idempotent: `formatUpgText(formatUpgText(x)) === formatUpgText(x)`.
 */
export function formatUpgText(text: string, opts: SerializeOptions = {}): string {
  return serializeCanonical(parseUpg(text), opts)
}

/** True iff `text` is already in canonical form (used by `upg fmt --check`). */
export function isCanonical(text: string): boolean {
  // Preserve the existing provenance so the check does not flag a benign
  // timestamp/source difference as non-canonical.
  const parsed = parseUpg(text)
  return text === serializeCanonical(parsed)
}

// ─── Header seal verification ───────────────────────────────────────────────
//
// `$upg.counts` and `$upg.integrity` are DERIVED: the serialiser computes both
// from the body it is about to write. Until now nothing read them back, so a
// file whose header and body had fallen out of step looked perfectly healthy to
// every reader — `normalizeDocument` drops both fields on the way in, and the
// in-memory document therefore has no memory of what the file CLAIMED.
//
// That gap is reachable without malice. Two branches each append one node to the
// same graph; git merges the body cleanly (different array positions) but sees
// `"nodes": 1273 → 1274` on both sides as the SAME one-line change and takes it
// once. The merged file declares 1274 and holds 1275, with no conflict marker
// anywhere. `upg fmt --check` catches it (byte-canonicality implies a fresh
// header) but any reader that goes straight to the body does not.
//
// `checkHeaderSeal` closes that: it compares a file's header against that same
// file's body. It is a self-consistency check on the ARTIFACT, so it needs no
// session state and makes no claim about anyone's in-memory edits.

/** One `$upg.counts` field whose declared value disagrees with the body. */
export interface UPGHeaderCountsMismatch {
  /** The counts key, e.g. `nodes`, `edges`, `products`, `cross_edges`. */
  field: string
  /** What `$upg.counts` claims (0 when the key is absent from the header). */
  declared: number
  /** What the body actually holds (0 when the key is not derived for this doc). */
  actual: number
}

/** The `$upg.integrity.body` seal disagreeing with a recomputation over the body. */
export interface UPGHeaderIntegrityMismatch {
  /** The algorithm label the header declares, e.g. `sha256-128`. */
  algorithm: string
  /** The checksum recorded in `$upg.integrity.body`. */
  declared: string
  /** The checksum recomputed from the body as it stands. */
  computed: string
}

/** Verdict of `checkHeaderSeal`: is a `.upg` file's header true to its own body? */
export interface UPGHeaderSealReport {
  /**
   * False for a legacy flat file with no `$upg` block. Nothing was declared, so
   * nothing can be stale: both drift arrays are empty and both `*_checked`
   * flags are false. Never treat a headerless file as drifted.
   */
  header_present: boolean
  /** True when `$upg.counts` was compared against the body. */
  counts_checked: boolean
  /** True when `$upg.integrity.body` was recomputed and compared. */
  integrity_checked: boolean
  /**
   * Why a check was skipped despite a header being present — an unrecognised
   * `format_version` (the body layout the seal was computed over may differ from
   * this serialiser's), an unrecognised `integrity.algorithm`, or a missing
   * block. Absent when everything applicable was checked.
   */
  skipped_reason?: string
  /** Per-field counts disagreements. Empty when the counts are true. */
  counts_drift: UPGHeaderCountsMismatch[]
  /** At most one entry: the body seal is either intact or it is not. */
  integrity_drift: UPGHeaderIntegrityMismatch[]
}

/**
 * Compare a parsed `.upg` object's `$upg` header against its own body.
 *
 * Both checks are gated on `format_version` matching this serialiser's
 * {@link UPG_CANONICAL_FORMAT_VERSION}: the counts keys and the canonical body
 * layout are contracts OF a format version, so a file written under a different
 * one must not be judged by this one's rules. A future format bump therefore
 * degrades to "not checked" rather than to a wall of false positives.
 *
 * Pure and read-only — it neither repairs the document nor touches the file.
 * `upg fmt` is the repair: it recomputes both fields from the body it writes.
 *
 * @param obj An already-parsed `.upg` document (canonical `$upg` envelope or
 *   legacy flat). Pass the RAW parse, not a normalised document — normalisation
 *   is what discards the header this function exists to read.
 */
export function checkHeaderSeal(obj: unknown): UPGHeaderSealReport {
  const raw = obj as Record<string, unknown>
  const header = raw?.$upg as UPGHeader | undefined

  const report: UPGHeaderSealReport = {
    header_present: !!header,
    counts_checked: false,
    integrity_checked: false,
    counts_drift: [],
    integrity_drift: [],
  }
  if (!header) return report

  if (header.format_version !== UPG_CANONICAL_FORMAT_VERSION) {
    report.skipped_reason =
      `format_version "${header.format_version}" is not this serialiser's ` +
      `"${UPG_CANONICAL_FORMAT_VERSION}"; counts keys and body layout are ` +
      `format-version contracts, so neither seal can be judged here.`
    return report
  }

  // Normalise once: the derivations and the checksum both operate on the
  // in-memory document shape, exactly as the serialiser did when it stamped
  // the header. parse → normalise → derive reproduces the write-time inputs.
  const doc = normalizeDocument(raw)

  // ── counts ────────────────────────────────────────────────────────────────
  const declaredCounts = (header.counts ?? {}) as Record<string, unknown>
  const actualCounts = deriveCounts(doc)
  if (header.counts === undefined) {
    report.skipped_reason = 'header has no `counts` block to compare.'
  } else {
    report.counts_checked = true
    // Union of both key sets: a header key we no longer derive is stale in one
    // direction (e.g. `watched_products: 1` left behind after the last watched
    // member was removed — the serialiser omits that key at zero), and a
    // derived key the header never mentions is stale in the other.
    const fields = new Set([...Object.keys(declaredCounts), ...Object.keys(actualCounts)])
    for (const field of [...fields].sort()) {
      const declaredRaw = declaredCounts[field]
      const declared = typeof declaredRaw === 'number' ? declaredRaw : 0
      const actual = actualCounts[field] ?? 0
      if (declared !== actual) report.counts_drift.push({ field, declared, actual })
    }
  }

  // ── integrity ─────────────────────────────────────────────────────────────
  const integrity = header.integrity
  if (!integrity || typeof integrity.body !== 'string') {
    report.skipped_reason = report.skipped_reason ?? 'header has no `integrity.body` seal to compare.'
  } else if (integrity.algorithm !== INTEGRITY_ALGORITHM) {
    // A different algorithm is not drift — we simply cannot recompute it. Say so
    // rather than reporting a guaranteed-mismatching digest as tampering.
    report.skipped_reason =
      report.skipped_reason ??
      `integrity.algorithm "${integrity.algorithm}" is not "${INTEGRITY_ALGORITHM}"; ` +
        `the declared digest cannot be recomputed here.`
  } else {
    report.integrity_checked = true
    const computed = computeBodyChecksum(doc)
    if (computed !== integrity.body) {
      report.integrity_drift.push({
        algorithm: integrity.algorithm,
        declared: integrity.body,
        computed,
      })
    }
  }

  return report
}

/**
 * Text-level form of {@link checkHeaderSeal}: strips a leading UTF-8 BOM, parses,
 * and checks. The BOM strip mirrors the load path — editors that prepend one
 * would otherwise make `JSON.parse` throw on a file that is otherwise valid.
 *
 * @throws SyntaxError when `text` is not valid JSON.
 */
export function checkHeaderSealText(text: string): UPGHeaderSealReport {
  const noBom = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text
  return checkHeaderSeal(JSON.parse(noBom))
}
